import './style.scss';

import * as XLSX from 'xlsx';
import xls from '../../assets/icons/xls.svg';
import { useCallback, useEffect, useState } from 'react';
import { formatDateInBogota } from '../../utils/dateTime';
import {getIdentificationTypeLabel} from '../../utils/identificationTypes';

const NATIONALITY_LABELS = {
  CO: 'Colombiano(a)',
  VE: 'Venezolano(a)'
};

function formatYesNo(value) {
  if (value === true || value === 'Si' || value === 'SI' || value === 'SÍ' || value === 1 || value === '1') {
    return 'SI';
  }
  if (value === false || value === 'No' || value === 'NO' || value === 0 || value === '0') {
    return 'NO';
  }
  return value ?? '';
}

function formatDate(value) {
  if (!value) return '';
  return formatDateInBogota(value);
}

function resolveGenderValue(gender, genders) {
  if (!genders?.length) return gender ?? '';
  const found = genders.find(g =>
    g.id?.toString() === gender?.toString() ||
    g.code?.toString() === gender?.toString() ||
    g.value?.toString() === gender?.toString()
  );
  return found?.value ?? gender ?? '';
}

function resolveNationality(value) {
  if (!value) return { label: '', other: '' };
  const normalized = value?.toString()?.trim();
  if (NATIONALITY_LABELS[normalized]) {
    return { label: NATIONALITY_LABELS[normalized], other: '' };
  }
  if (normalized === 'OT') {
    return { label: 'Otro', other: '' };
  }
  return { label: 'Otro', other: normalized };
}

function resolveOtherField(mainValue, otherValue) {
  const normalized = String(mainValue ?? '').trim().toLowerCase();
  if (normalized === 'otro') {
    return otherValue ?? '';
  }
  return '';
}

function resolveSchoolName(beneficiary) {
  return beneficiary?.school?.name ?? beneficiary?.school?.address ?? '';
}

function resolveSchoolCode(beneficiary) {
  return beneficiary?.school?.code ?? '';
}

function resolveSchoolMunicipalityName(beneficiary) {
  return beneficiary?.school?.municipality?.name ?? '';
}

function resolveMediatorName(beneficiary) {
  return String(
    beneficiary?.school?.user?.full_name ??
      beneficiary?.school?.user?.fullName ??
      beneficiary?.user?.full_name ??
      beneficiary?.user?.fullName ??
      ''
  ).trim();
}

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function calculateBeneficiaryAge(beneficiary, now = new Date()) {
  let age = beneficiary?.age;
  if ((age === null || age === undefined) && beneficiary?.birth_date) {
    const birthday = new Date(beneficiary.birth_date);
    if (!Number.isNaN(birthday.getTime())) {
      age = now.getFullYear() - birthday.getFullYear();
      const month = now.getMonth() - birthday.getMonth();
      if (month < 0 || (month === 0 && now.getDate() < birthday.getDate())) {
        age--;
      }
    }
  }

  const numericAge = Number(age);
  return Number.isFinite(numericAge) && numericAge >= 0 ? numericAge : null;
}

function resolveCopAgeRange(beneficiary, age) {
  const storedRange = String(
    beneficiary?.grade_or_study ?? beneficiary?.gradeOrStudy ?? ''
  ).trim();
  if (storedRange) return storedRange;

  if (!Number.isFinite(age)) return '';
  if (age <= 5) return 'Primera infancia 0-5';
  if (age <= 17) return 'Ninez-adolescencia 6-17';
  if (age <= 28) return 'Juventud 18-28';
  if (age <= 54) return 'Adulto/a 29-54';
  return 'Adulto mayor 55 o mas';
}

function pushIfMissing(values, nextValue) {
  const normalizedNextValue = normalizeText(nextValue);
  if (!normalizedNextValue) return;

  const exists = values.some(value => normalizeText(value) === normalizedNextValue);
  if (!exists) values.push(nextValue);
}

function resolveCopDifferentialFocus(beneficiary) {
  const values = [];
  const ethnicValue = String(
    beneficiary?.ethnic_group_value ?? beneficiary?.ethnicGroupValue ?? ''
  ).trim();
  const riskValue = String(
    beneficiary?.risk_factor_value ?? beneficiary?.riskFactorValue ?? ''
  ).trim();
  const disabilityValue = String(
    beneficiary?.disability_value ??
      beneficiary?.disabilityValue ??
      beneficiary?.disability ??
      ''
  ).trim();

  if (ethnicValue && normalizeText(ethnicValue) !== 'ninguna') {
    ethnicValue.split(',').forEach(value => pushIfMissing(values, value.trim()));
  }
  if (formatYesNo(beneficiary?.lgbtiq) === 'SI') {
    pushIfMissing(values, 'LGBTIQ+');
  }
  if (normalizeText(riskValue).includes('calle')) {
    pushIfMissing(values, 'Situacion de calle');
  }
  if (formatYesNo(beneficiary?.farmer) === 'SI') {
    pushIfMissing(values, 'Campesino');
  }
  if (
    disabilityValue &&
    !normalizeText(disabilityValue).includes('no tiene') &&
    normalizeText(disabilityValue) !== 'ninguna'
  ) {
    pushIfMissing(values, 'Discapacidad');
  }

  return values.join(', ');
}

function resolveCopOrganization(beneficiary) {
  return String(beneficiary?.observations ?? '').trim();
}

function resolveCopVictimPopulation(beneficiary) {
  return formatYesNo(
    beneficiary?.victim_population_value ??
      beneficiary?.victimPopulationValue ??
      beneficiary?.victim_population ??
      beneficiary?.victimPopulation
  );
}

const REPORT_MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

function resolveReportMonth(activityGroup, participationDate) {
  const reportMonth = Number(
    activityGroup?.month ?? activityGroup?.reportMonth ?? activityGroup?.report_month
  );

  if (Number.isInteger(reportMonth) && reportMonth >= 1 && reportMonth <= 12) {
    return REPORT_MONTH_NAMES[reportMonth - 1];
  }

  const fallbackDate = activityGroup?.createdAt ??
    activityGroup?.created_at ??
    participationDate;
  if (!fallbackDate) return '';

  const parsedDate = new Date(fallbackDate);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return REPORT_MONTH_NAMES[parsedDate.getMonth()] ?? '';
}

function buildSocialControlBeneficiaryInfo(beneficiary, genders, age) {
  const activityGroup =
    beneficiary?.activityGroup ?? beneficiary?.activity_group ?? {};
  const activity = activityGroup?.activity ?? {};
  const participationDate = beneficiary?.createdAt ?? beneficiary?.created_at;

  return {
    'TIPO DE DOCUMENTO':
      getIdentificationTypeLabel(beneficiary?.document_type),
    'NUMERO DE DOCUMENTO': beneficiary?.document ?? '',
    'NOMBRES Y APELLIDOS COMPLETOS': beneficiary?.full_name ?? '',
    ORGANIZACION: resolveCopOrganization(beneficiary),
    'EMPRESA/ENTIDAD/DEPENDENCIA': beneficiary?.address ?? '',
    'CARGO/EMPLEO/CALIDAD EN LA QUE ACTUA':
      beneficiary?.occupation_value ?? '',
    'SEXO (HOMBRE/MUJER)': resolveGenderValue(beneficiary?.gender, genders),
    'RANGO EDAD': resolveCopAgeRange(beneficiary, age),
    'ENFOQUE DIFERENCIAL': resolveCopDifferentialFocus(beneficiary),
    'POBLACION VICTIMA': resolveCopVictimPopulation(beneficiary),
    'COMUNA EN LA QUE RESIDE':
      beneficiary?.municipality?.name ?? beneficiary?.municipality_id ?? '',
    'CORREO ELECTRONICO': beneficiary?.email ?? '',
    'NUMERO DE CONTACTO': beneficiary?.cellphone ?? '',
    'ACTIVIDAD EN LA QUE PARTICIPO': activity?.name ?? '',
    'LINEA DE LA ACTIVIDAD': activity?.line ?? '',
    'FECHA EN LA QUE PARTICIPO': formatDate(participationDate),
    'MES DEL REPORTE': resolveReportMonth(activityGroup, participationDate)
  };
}

function DownloadBeneficiaries({
  beneficiaries,
  genders,
  buttonText,
  buttonIcon = 'download',
  iconClassName = 'btn__icon',
  fileName,
  containerClassName,
  buttonClassName,
  disabled = false,
  schoolNameColumnLabel = 'NOMBRE IE',
  processColumnLabel = 'PROCESO FORMATIVO',
  includeGroupColumns = true,
  processMunicipalityColumnLabel = 'COMUNA PROCESO FORMATIVO',
  characterizationMode = 'default'
}) {
  const [beneficiariesInfo, setBeneficiariesInfo] = useState([]);

  const generateInfo = useCallback(() => {
    const now = new Date();
    const list = Array.isArray(beneficiaries) ? beneficiaries : [];
    const info = list.map(b => {
      const age = calculateBeneficiaryAge(b, now);

      const genderValue = resolveGenderValue(b.gender, genders);
      const idTypeLabel = getIdentificationTypeLabel(b.document_type);
      const nationalityInfo = resolveNationality(b.nationality);
      const orientationLabel = b.sexual_orientation_value || b.sexual_orientation;
      const disabilityLabel = b.disability_value || b.disability;
      const riskLabel = b.risk_factor_value || b.risk_factor;

      if (characterizationMode === 'social_control') {
        return buildSocialControlBeneficiaryInfo(b, genders, age);
      }

      if (characterizationMode === 'cop') {
        return {
          'TIPO DE DOCUMENTO': idTypeLabel,
          'NUMERO DE DOCUMENTO': b.document ?? '',
          'NOMBRES Y APELLIDOS COMPLETOS': b.full_name ?? '',
          'NOMBRE ASESORIA': resolveSchoolName(b),
          'CODIGO ASESORIA': resolveSchoolCode(b),
          'COMUNA ASESORIA': resolveSchoolMunicipalityName(b),
          MEDIADOR: resolveMediatorName(b),
          'EMPRESA/ENTIDAD/DEPENDENCIA': b.address ?? '',
          'CARGO/EMPLEO/CALIDAD EN LA QUE ACTUA': b.occupation_value ?? '',
          'SEXO (HOMBRE/MUJER)': genderValue,
          'RANGO EDAD': resolveCopAgeRange(b, age),
          'ENFOQUE DIFERENCIAL': resolveCopDifferentialFocus(b),
          'POBLACION VICTIMA': resolveCopVictimPopulation(b),
          'COMUNA EN LA QUE RESIDE':
            b.municipality?.name ?? b.municipality_id ?? '',
          'CORREO ELECTRONICO': b.email ?? '',
          ORGANIZACION: resolveCopOrganization(b),
          'NUMERO DE CONTACTO': b.cellphone ?? ''
        };
      }

      return {
        'NOMBRE COMPLETO': b.full_name,
        'TIPO DE IDENTIFICACION': idTypeLabel,
        'NRO IDENTIFICACION': b.document,
        'FECHA DE NACIMIENTO': formatDate(b.birth_date),
        'SEXO AL NACER': genderValue,
        NACIONALIDAD: nationalityInfo.label,
        'ESPECIFIQUE NACIONALIDAD': nationalityInfo.other,
        DIRECCION: b.address ?? '',
        'TELEFONO DE CONTACTO': b.cellphone ?? '',
        'CORREO ELECTRONICO': b.email ?? '',
        ZONA: b.zone?.name ?? b.zone_id ?? '',
        'COMUNA/CORREGIMIENTO': b.municipality?.name ?? b.municipality_id ?? '',
        'BARRIO/VEREDA': b.neighborhood?.name ?? b.neighborhood_id ?? '',
        'ESTUDIA ACTUALMENTE': formatYesNo(b.study_currently),
        'GRADO O ESTUDIO QUE CURSA': b.grade_or_study ?? '',
        'NIVEL DE ESCOLARIDAD': b.scholarship_value ?? '',
        'PERTENECE A LGBTIQ+': formatYesNo(b.lgbtiq),
        'ORIENTACION SEXUAL': orientationLabel ?? '',
        'ESPECIFIQUE ORIENTACION SEXUAL': resolveOtherField(orientationLabel, b.sexual_orientation),
        ETNIA: b.ethnic_group_value ?? '',
        'POBLACION VICTIMA': b.victim_population_value ?? '',
        'SE IDENTIFICA COMO CAMPESINO': formatYesNo(b.farmer),
        'ENFOQUE DE DISCAPACIDAD': disabilityLabel ?? '',
        'PROBLEMATICAS Y FACTORES DE RIESGO': riskLabel ?? '',
        OBSERVACIONES: b.observations ?? '',
        'NOMBRE DEL ACUDIENTE': b.parent ?? '',
        'TELEFONO DEL ACUDIENTE': b.parent_phone ?? '',
        'CORREO ELECTRONICO DEL ACUDIENTE': b.parent_email ?? '',
        'ACEPTA POLITICAS DE DATOS': formatYesNo(b.data_policy_accepted),
        [schoolNameColumnLabel]: resolveSchoolName(b),
        [processColumnLabel]: b.school?.code ?? '',
        ...(includeGroupColumns ? {
          GRUPO: b.group?.code ?? '',
          'ID GRUPO': b.group?.id ?? ''
        } : {}),
        [processMunicipalityColumnLabel]: b.school?.municipality?.name ?? '',
        MEDIADOR: resolveMediatorName(b),
        'FECHA REGISTRO': formatDate(b.created_at)
      };
    });
    setBeneficiariesInfo(info);
  }, [
    beneficiaries,
    genders,
    characterizationMode,
    includeGroupColumns,
    processColumnLabel,
    processMunicipalityColumnLabel,
    schoolNameColumnLabel
  ]);

  function handleCreateXLS() {
    if (disabled || beneficiariesInfo.length === 0) return;

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    // Create a worksheet
    const ws = XLSX.utils.json_to_sheet(beneficiariesInfo);

    // Set column widths
    const columnCount = beneficiariesInfo?.[0]
      ? Object.keys(beneficiariesInfo[0]).length
      : 0;
    ws['!cols'] = Array.from({ length: columnCount }, () => ({ wch: 26 }));

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // Generate a Blob from the workbook
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });

    // Create a download link and click it programmatically to download the file
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'caracterizaciones.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    generateInfo();
  }, [generateInfo]);

  return (
    <div className={containerClassName || 'download-container'}>
      {buttonText ? (
        <button
          onClick={handleCreateXLS}
          title={'Descargar Beneficiarios'}
          className={buttonClassName || 'download-btn'}
          disabled={disabled || beneficiariesInfo.length === 0}>
          <span className={`material-symbols-rounded ${iconClassName}`}>
            {buttonIcon}
          </span>
          <span className='btn__text'>{buttonText}</span>
        </button>
      ) : (
        <button
          onClick={handleCreateXLS}
          title={'Descargar Beneficiarios'}
          disabled={disabled || beneficiariesInfo.length === 0}>
          <img src={xls} alt='download' />
        </button>
      )}
    </div>
  );
}

export default DownloadBeneficiaries;
